<template>
  <TabPanel :id="`id${id}`" ref="panel" :value="value || title">
    <slot />
  </TabPanel>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from "vue";
import TabPanel from "primevue/tabpanel";
import injectAsserted from "@/functions/assert/injectAsserted";
import { useFormValidation } from "@/modules/formValidation";
import { TabConfigInjectionKey, type TabConfig } from "./TabInjectionKey";

const props = withDefaults(
  defineProps<{
    title: string;
    value: string;
    disabled?: boolean;
    separatorBefore?: boolean;
    iconSettings?: {
      class: string;
      style?: string;
      command?: () => void;
    };
    ignoreValidation?: boolean;
  }>(),
  {
    disabled: false,
    separatorBefore: false,
    iconSettings: () => ({
      class: "",
      style: "",
      command: () => {},
    }),
    ignoreValidation: false,
  },
);

const config = injectAsserted(TabConfigInjectionKey);
const id = crypto.randomUUID();
const active = computed(
  () =>
    config.validationScope.value === "all" ||
    config.activeValue.value === (props.value || props.title),
);

const validation = useFormValidation({
  name: () => `TabChild:${props.value || props.title}`,
  displayName: () => props.title,
  disabled: () => props.disabled,
  active,
  ignoreValidation: () => props.ignoreValidation,
  path: () => [props.title],
});

const ownConfig = computed<TabConfig>(() => ({
  id,
  title: props.title,
  value: props.value || props.title,
  validation,
  disabled: props.disabled,
  separatorBefore: props.separatorBefore,
  iconSettings: props.iconSettings,
}));

onMounted(() => {
  config.registerTabs.value.push(ownConfig);
});

onUnmounted(() => {
  config.registerTabs.value = config.registerTabs.value.filter((tab) => tab.value.id !== id);
});
</script>
